import React, { useEffect, useState } from 'react';
import cidLogo from '../../assets/cid_logo.png';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, LogIn } from 'lucide-react';
import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../store/authStore';
import { ROUTES } from '../../config/routes.config';
import { completeLoginRedirect } from '../../utils/qrSession';

// KGID (or username) + PIN. KGID is the admin-defined User Profile field; PIN is
// the account password. No OTP — the PIN alone completes sign-in.
const loginSchema = z.object({
  kgid: z.string().min(1, 'KGID or username is required'),
  pin: z.string().min(1, 'PIN is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { kgid: '', pin: '' },
  });

  useEffect(() => {
    document.title = 'RRMS | Sign in';
  }, []);

  const onSubmit = async (values: LoginFormValues) => {
    try {
      setLoading(true);
      const res = await authService.login(values.kgid.trim(), values.pin);
      if (!res.success || !res.token || !res.data) {
        toast.error(res.message || 'Invalid credentials.');
        return;
      }
      // Resolve (and admit any QR case) before login() flips isAuthenticated,
      // since GuestGuard redirects as soon as that happens.
      const redirectTo =
        completeLoginRedirect((location.state as { from?: string })?.from) || ROUTES.DASHBOARD;
      login({ user: res.data, token: res.token });
      navigate(redirectTo, { replace: true });
      toast.success('Signed in.');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Connection failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputBase =
    'w-full rounded-md border border-[var(--color-gray-300)] px-4 py-2.5 text-base outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-light)] placeholder:text-[var(--color-gray-400)]';

  return (
    <div className="min-h-screen flex bg-[var(--bg-page)]">
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[var(--bg-sidebar)] text-white">
        <div className="max-w-md px-8 flex flex-col items-center text-center">
          <img src={cidLogo} alt="CID Logo" style={{ height: 128, width: 128, objectFit: 'contain', marginBottom: '1.75rem' }} />
          <h1 className="text-3xl font-bold mb-3 leading-snug">Records Room Management System</h1>
          <p className="text-base text-white/80 leading-relaxed">Secure digital management of police case records for the Government of Karnataka.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md card px-10 py-10">
          <div className="flex items-center gap-3 mb-5">
            <img src={cidLogo} alt="CID Logo" style={{ height: 52, width: 52, objectFit: 'contain' }} />
            <h2 className="text-2xl font-bold text-[var(--color-primary)]">Sign in to RRMS</h2>
          </div>

          <p className="text-md text-muted mb-6">Enter your KGID number (or username) and PIN to continue.</p>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            <div>
              <label className="block text-md font-semibold mb-1.5">KGID Number / Username</label>
              <input
                type="text"
                {...register('kgid')}
                className={inputBase}
                autoComplete="username"
                placeholder="Enter your KGID number or username"
                disabled={loading}
                autoFocus
              />
              {errors.kgid && <p className="mt-1.5 text-md text-[var(--color-error)]">{errors.kgid.message}</p>}
            </div>

            <div>
              <label className="block text-md font-semibold mb-1.5">PIN</label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  {...register('pin')}
                  className={`${inputBase} pr-11`}
                  autoComplete="current-password"
                  placeholder="Enter your PIN"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPin((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-gray-400)] hover:text-[var(--color-gray-600)]"
                  tabIndex={-1}
                  aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                >
                  {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.pin && <p className="mt-1.5 text-md text-[var(--color-error)]">{errors.pin.message}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-primary)] px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-[var(--color-primary-light)] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <LogIn size={18} /> {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
